/**
 * TieredGate — 3-tier freemium gate replacing SubscriptionGate.
 *
 * Tier 1: Free access (render children directly, limits enforced by API)
 * Tier 2: Requires ad unlock before access → shows AdUnlockPrompt
 * Tier 3: Premium only → shows existing SubscriptionGate paywall
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { isSubscriptionActive } from '../../services/subscriptionService';
import { getFreemiumStatus, FreemiumFeatureType } from '../../services/tokenService';
import SubscriptionGate from './SubscriptionGate';
import AdUnlockPrompt from './AdUnlockPrompt';

interface TieredGateProps {
  tier: 1 | 2 | 3;
  featureType?: FreemiumFeatureType;
  feature?: string;
  children: React.ReactNode;
}

const TieredGate: React.FC<TieredGateProps> = ({ tier, featureType, feature, children }) => {
  const { user, isAuthenticated } = useAuth();
  const [adUnlocked, setAdUnlocked] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  // Subscribers, admin, and trialing users bypass all gates
  const isPrivileged = user?.role === 'admin' || (user?.subscriptionStatus && isSubscriptionActive(user.subscriptionStatus));

  // For Tier 2, check ad unlock status
  useEffect(() => {
    if (tier !== 2 || isPrivileged || !isAuthenticated || !featureType) {
      return;
    }

    let cancelled = false;
    setLoading(true);

    getFreemiumStatus()
      .then((status) => {
        if (cancelled) return;
        if (status.isSubscriber) {
          setAdUnlocked(true);
        } else {
          const featureStatus = status.features[featureType];
          setAdUnlocked(featureStatus?.adUnlocked ?? false);
        }
      })
      .catch(() => {
        if (!cancelled) setAdUnlocked(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tier, featureType, isPrivileged, isAuthenticated]);

  // Not authenticated — delegate to SubscriptionGate (shows login prompt)
  if (!isAuthenticated || !user) {
    return <SubscriptionGate feature={feature}>{children}</SubscriptionGate>;
  }

  // Privileged users always pass through
  if (isPrivileged) {
    return <>{children}</>;
  }

  // Tier 1: Free direct access
  if (tier === 1) {
    return <>{children}</>;
  }

  // Tier 2: Ad-gated
  if (tier === 2 && featureType) {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
          <div className="loading-spinner" />
        </div>
      );
    }

    if (!adUnlocked) {
      return (
        <AdUnlockPrompt
          featureType={featureType}
          onUnlocked={() => setAdUnlocked(true)}
        />
      );
    }

    return <>{children}</>;
  }

  // Tier 3: Premium only — show paywall
  return <SubscriptionGate feature={feature}>{children}</SubscriptionGate>;
};

export default TieredGate;
